var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useMutation, useQueryClient, } from 'react-query';
import { useDataProvider } from 'react-admin';
import { omit } from 'lodash';
import { useCallback } from 'react';
/**
 * Returns a function allowing to call the dataProvider.unlock() method, as well as
 * the react-query mutation handle.
 *
 * @param {string} resource
 * @param {UnlockParams} params The unlock function parameters { id, identity, meta }
 * @param {Object} options React Query Mutation Options object
 *
 * @returns The `unlock()` callback as well as the react-query mutation object.
 *
 * @example Simple Usage
 *
 * ```tsx
 * const [unlock, _mutation] = useUnlock('posts', { id: 123, identity: 'mario' });
 * React.useEffect(() => {
 *     unlock();
 * }, []);
 * ```
 *
 * @example Call unlock with parameters
 *
 * ```tsx
 * const [unlock, _mutation] = useUnlock();
 * React.useEffect(() => {
 *     unlock('posts', { id: 123, identity: 'mario' });
 * }, []);
 * ```
 */
export function useUnlock(resource, params, options) {
    if (options === void 0) { options = {}; }
    var dataProvider = useDataProvider();
    var queryClient = useQueryClient();
    var updateCache = function (_a) {
        var resource = _a.resource, params = _a.params;
        var updateColl = function (old) {
            if (!old)
                return;
            var recordId = params.id;
            if (!recordId)
                return old;
            var index = old.findIndex(function (lock) { return lock.recordId === recordId; });
            if (index === -1) {
                return old;
            }
            return __spreadArray(__spreadArray([], old.slice(0, index), true), old.slice(index + 1), true);
        };
        queryClient.invalidateQueries([
            resource,
            'getLock',
            omit(params, ['identity']),
        ]);
        queryClient.setQueriesData([resource, 'getLocks', omit(params, ['identity', 'id'])], function (res) { return (res && res.length ? updateColl(res) : res); });
    };
    var mutation = useMutation(function (_a) {
        var _b = _a === void 0 ? {} : _a, _c = _b.resource, callTimeResource = _c === void 0 ? resource : _c, _d = _b.params, callTimeParams = _d === void 0 ? params : _d;
        return dataProvider.unlock(callTimeResource, callTimeParams);
    }, __assign(__assign({}, options), { onSuccess: function (data, variables, context) {
            updateCache({
                resource: (variables === null || variables === void 0 ? void 0 : variables.resource) || resource,
                params: (variables === null || variables === void 0 ? void 0 : variables.params) || params,
            });
            if (options.onSuccess) {
                options.onSuccess(data, variables, context);
            }
        } }));
    var unlock = useCallback(function (callTimeResource, callTimeParams, mutateOptions) {
        if (callTimeResource === void 0) { callTimeResource = resource; }
        if (callTimeParams === void 0) { callTimeParams = params; }
        if (mutateOptions === void 0) { mutateOptions = options; }
        return mutation.mutate({ resource: callTimeResource, params: callTimeParams }, mutateOptions);
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mutation.mutate, JSON.stringify({ resource: resource, params: params, options: options })]);
    return [unlock, mutation];
}
